/*
Copyright (c) NAVER Corp.
name: @egjs/vue-infinitegrid
license: undefined
author: NAVER Corp.
repository: https://github.com/naver/egjs-infinitegrid/tree/main/packages/vue-infinitegrid
version: 4.12.0
*/
import VanillaInfiniteGrid, { FrameInfiniteGrid as FrameInfiniteGrid$1, JustifiedInfiniteGrid as JustifiedInfiniteGrid$1, MasonryInfiniteGrid as MasonryInfiniteGrid$1, PackingInfiniteGrid as PackingInfiniteGrid$1, Renderer, INFINITEGRID_EVENTS, mountRenderingItems, INFINITEGRID_METHODS, getRenderingItems, ITEM_TYPE } from '@egjs/infinitegrid';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
var _SuppressedError = typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
  var e = new Error(message);
  return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var VUE_INFINITEGRID_PROPS = ["tag", "status", "useFirstRender", "itemBy", "groupBy", "infoBy"];

var FrameInfiniteGrid = makeInfiniteGrid("frame-infinite-grid", FrameInfiniteGrid$1);

var JustifiedInfiniteGrid = makeInfiniteGrid("justified-infinite-grid", JustifiedInfiniteGrid$1);

var MasonryInfiniteGrid = makeInfiniteGrid("masonry-infinite-grid", MasonryInfiniteGrid$1);

var PackingInfiniteGrid = makeInfiniteGrid("packing-infinite-grid", PackingInfiniteGrid$1);

function decamelize(str) {
  return str.replace(/([a-z])([A-Z])/g, function (_, letter, letter2) {
    return "".concat(letter, "-").concat(letter2.toLowerCase());
  });
}

function makeInfiniteGrid(tagName, GridClass) {
  var propertyTypes = GridClass.propertyTypes,
    defaultOptions = GridClass.defaultOptions;
  var watch = {};
  var _loop_1 = function (name) {
    watch[name] = function (value) {
      this.$_grid[name] = value;
    };
  };
  for (var name in propertyTypes) {
    _loop_1(name);
  }
  var methods = {};
  INFINITEGRID_METHODS.forEach(function (name) {
    methods[name] = function () {
      var _a;
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      return (_a = this.$_grid)[name].apply(_a, args);
    };
  });
  methods.$_getItemInfos = function () {
    var props = this.$props;
    var slots = this.$slots.default;
    var itemBy = props.itemBy || function (item, i) {
      var key = item.key;
      return key == null ? i : key;
    };
    var attributePrefix = props.attributePrefix || VanillaInfiniteGrid.defaultOptions.attributePrefix;
    var groupBy = props.groupBy || function (item) {
      var _a;
      var itemProps = item.props || ((_a = item.data) === null || _a === void 0 ? void 0 : _a.attrs);
      return itemProps ? itemProps["".concat(attributePrefix, "groupkey")] : undefined;
    };
    var infoBy = props.infoBy || function () {
      return {};
    };
    var children = [];
    if (Array.isArray(slots)) {
      children = slots;
    }
    // Check Vue3 Slot Type
    return children.map(function (child, i) {
      var _a = infoBy(child, i) || {},
        data = _a.data,
        rest = __rest(_a, ["data"]);
      return __assign(__assign({
        groupKey: groupBy(child, i),
        key: itemBy(child, i)
      }, rest), {
        data: __assign(__assign({}, data), {
          vnode: child
        })
      });
    });
  };
  methods.$_getVisibleChildren = function () {
    var props = this.$props;
    var scopedSlots = "$scopedSlots" in this && this.$scopedSlots || "$slots" in this && this.$slots || {};
    var placeholder = scopedSlots.placeholder;
    var loading = scopedSlots.loading;
    var visibleItems = getRenderingItems(this.$_getItemInfos(), {
      grid: "$_grid" in this ? this.$_grid : null,
      status: props.status,
      horizontal: props.horizontal,
      useFirstRender: props.useFirstRender,
      usePlaceholder: !!scopedSlots.placeholder,
      useLoading: !!scopedSlots.loading
    });
    return visibleItems.map(function (item) {
      if (item.type === ITEM_TYPE.VIRTUAL) {
        return placeholder({
          item: item
        });
      } else if (item.type === ITEM_TYPE.LOADING) {
        return loading({
          item: item
        });
      } else {
        return item.data.vnode;
      }
    });
  };
  methods.$_renderContainer = function (h) {
    var props = this.$props;
    var visibleChildren = this.$_getVisibleChildren();
    var container = props.container;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var containerTag = props.containerTag || "div";
    if (container === true) {
      return [h(containerTag, {
        ref: "containerRef"
      }, visibleChildren)];
    }
    return visibleChildren;
  };
  return {
    name: tagName,
    props: __spreadArray(__spreadArray([], VUE_INFINITEGRID_PROPS, true), Object.keys(defaultOptions), true),
    watch: watch,
    methods: methods,
    render: function (h) {
      var props = this.$props;
      var tag = props.tag || "div";
      return h(tag, {
        ref: "wrapperRef"
      }, this.$_renderContainer(h));
    },
    created: function () {
      this.$_renderer = new Renderer();
    },
    mounted: function () {
      var _this = this;
      var defaultOptions = GridClass.defaultOptions;
      var options = {};
      var props = this.$props;
      var containerElement = this.$refs.containerRef;
      for (var name in defaultOptions) {
        if (name in props && typeof props[name] !== "undefined") {
          options[name] = props[name];
        }
      }
      if (containerElement) {
        options.container = containerElement;
      }
      options.renderer = this.$_renderer;
      var grid = new GridClass(this.$refs.wrapperRef, options);
      var _loop_2 = function (name) {
        var eventName = INFINITEGRID_EVENTS[name];
        var vueEventName = decamelize(eventName);
        grid.on(eventName, function (e) {
          _this.$emit(vueEventName, e);
        });
      };
      for (var name in INFINITEGRID_EVENTS) {
        _loop_2(name);
      }
      this.$_grid = grid;
      this.$_renderer.on("requestUpdate", function () {
        _this.$forceUpdate();
      });
      var scopedSlots = "$scopedSlots" in this && this.$scopedSlots || "$slots" in this && this.$slots || {};
      mountRenderingItems(this.$_getItemInfos(), {
        grid: this.$_grid,
        status: props.status,
        horizontal: props.horizontal,
        useFirstRender: props.useFirstRender,
        usePlaceholder: !!scopedSlots.placeholder,
        useLoading: !!scopedSlots.loading
      });
      this.$_renderer.updated();
    },
    updated: function () {
      this.$_renderer.updated();
    },
    beforeDestroy: function () {
      this.$_grid.destroy();
    }
  };
}
function install(app) {
  app.component("masonry-infinite-grid", MasonryInfiniteGrid);
  app.component("justified-infinite-grid", JustifiedInfiniteGrid);
  app.component("frame-infinite-grid", FrameInfiniteGrid);
  app.component("packing-infinite-grid", PackingInfiniteGrid);
}

export { FrameInfiniteGrid, JustifiedInfiniteGrid, MasonryInfiniteGrid, PackingInfiniteGrid, install, makeInfiniteGrid };
//# sourceMappingURL=infinitegrid.esm.js.map
